'use client';

import { useMemo } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { BlockList } from './BlockList';
import { groupArticleBlocks, mergeArticleBlocks, type GroupedBlocks } from './articleBlocks';
import type { ArticleBlock, BlockInstance } from './types';

/**
 * Helpers for themed BlogDetailLayout / WikiDetailLayout overrides to
 * opt into the article-block-overrides pipeline.
 *
 * <p>Background. The system blog/wiki detail layouts wire the override
 * pipeline natively: they call {@link mergeArticleBlocks}(template,
 * perArticle), {@link groupArticleBlocks}(...), then render the four
 * zones (top, bottom, sidebar, inline-after-paragraph-N). Themes that
 * REPLACE the system layout (e.g. lumen's {@code LumenBlogDetailLayout})
 * own all the markup themselves, so they had no idea overrides
 * existed — merge/render never happened.
 *
 * <p>Two ways for a themed layout to opt in:
 * <ol>
 *   <li>Drop in {@link ArticleTopBlocks} / {@link ArticleBottomBlocks} /
 *       {@link ArticleSidebarBlocks} wherever you want those blocks to
 *       render. 90% case — three components, three placements, no
 *       thinking.</li>
 *   <li>Call {@link useArticleBlockGroups} yourself for full control.
 *       Returns the grouped blocks. Useful when your layout needs
 *       inline blocks woven into its own markdown body renderer.</li>
 * </ol>
 *
 * <p>The {@code kind} parameter chooses where on the host's
 * content-module config the template lives — {@code 'blog'} →
 * {@code blog.articleTemplate.blocks}, {@code 'wiki'} →
 * {@code wiki.pageTemplate.blocks}. The per-content overrides live on
 * {@code article.blocks} (or {@code page.blocks}) and are the same
 * shape regardless.
 */

interface ArticleLike {
  blocks?: ArticleBlock[];
}

// Typed loosely so the helper is resilient if a host passes a slightly
// different shape — the fields we read are checked defensively.
type AnyContentConfig = Record<string, unknown> | undefined;

/**
 * Compute the grouped blocks for a given content piece. Does the
 * merge: template (from shell config) + per-content overrides (from
 * {@code article.blocks} / {@code page.blocks}), additively. Returns
 * the grouped result keyed by placement zone.
 *
 * <p>Memoized on {@code (article.blocks, config)} so React doesn't
 * re-merge every render.
 */
export function useArticleBlockGroups(
  article: ArticleLike,
  config: AnyContentConfig,
  kind: 'blog' | 'wiki',
): GroupedBlocks {
  return useMemo(() => {
    const tplBlocks =
      kind === 'blog'
        ? (
            (config as Record<string, unknown> | undefined)?.articleTemplate as
              | { blocks?: ArticleBlock[] }
              | undefined
          )?.blocks
        : (
            (config as Record<string, unknown> | undefined)?.pageTemplate as
              | { blocks?: ArticleBlock[] }
              | undefined
          )?.blocks;
    const merged = mergeArticleBlocks(tplBlocks, article.blocks);
    return groupArticleBlocks(merged);
  }, [article.blocks, config, kind]);
}

interface ZoneProps {
  article: ArticleLike;
  config: AnyContentConfig;
  kind: 'blog' | 'wiki';
  /** Inherited theme id from the surrounding layout — required so
   *  inner blocks resolve against the correct theme. Article/wiki
   *  detail pages render OUTSIDE a {@link BlockRenderer}. */
  themeId?: string | null;
  /** Optional className applied to a wrapper div around the rendered
   *  blocks. The wrapper is omitted entirely when there are no blocks
   *  AND when neither className nor style is provided.
   *
   *  <p>JSON-serializable — safe to pass from Server Components since
   *  this module is a Client Component (uses {@code useMemo}
   *  internally). A {@code wrapper: (children) => ReactNode} prop
   *  would be rejected on the Server→Client boundary. */
  className?: string;
  style?: CSSProperties;
}

function maybeWrap(content: ReactNode, className?: string, style?: CSSProperties): ReactNode {
  if (!className && !style) return content;
  return (
    <div className={className} style={style}>
      {content}
    </div>
  );
}

/** Top-of-content blocks. Renders nothing if no top blocks configured. */
export function ArticleTopBlocks({
  article,
  config,
  kind,
  themeId,
  className,
  style,
}: ZoneProps): ReactNode {
  const groups = useArticleBlockGroups(article, config, kind);
  if (groups.top.length === 0) return null;
  return maybeWrap(
    <BlockList items={groups.top as BlockInstance[]} themeId={themeId} />,
    className,
    style,
  );
}

/** Bottom-of-content blocks. */
export function ArticleBottomBlocks({
  article,
  config,
  kind,
  themeId,
  className,
  style,
}: ZoneProps): ReactNode {
  const groups = useArticleBlockGroups(article, config, kind);
  if (groups.bottom.length === 0) return null;
  return maybeWrap(
    <BlockList items={groups.bottom as BlockInstance[]} themeId={themeId} />,
    className,
    style,
  );
}

/** Sidebar blocks. Themes that have no aside element can skip rendering
 *  this; nothing else assumes the sidebar exists. */
export function ArticleSidebarBlocks({
  article,
  config,
  kind,
  themeId,
  className,
  style,
}: ZoneProps): ReactNode {
  const groups = useArticleBlockGroups(article, config, kind);
  if (groups.sidebar.length === 0) return null;
  return maybeWrap(
    <BlockList items={groups.sidebar as BlockInstance[]} themeId={themeId} />,
    className,
    style,
  );
}

/**
 * Inline blocks aren't rendered as a single component because each
 * inline block is bound to a specific paragraph index. Themes that
 * want inline support pull the map from
 * {@link useArticleBlockGroups}'s {@code .inline} field and render
 * each entry after its paragraph.
 *
 * <p>For themes without complex markdown rendering, inline support is
 * optional — just don't render the inline map. Operators who chose
 * {@code 'inline-after-paragraph-3'} will see those blocks fall
 * through silently. The composer doesn't promise inline placement
 * works on every theme; it depends on the theme's body renderer.
 */

// =============================================================================
// Index zones
// =============================================================================
//
// The blog/wiki INDEX has its own decoration: aboveBlocks + belowBlocks
// on the host's BlogModuleConfig / WikiModuleConfig. Themes that
// override BlogIndexLayout / WikiIndexLayout opt into rendering them
// here, exactly like the article-detail zones above.
//
// Why a separate component instead of reusing ArticleTopBlocks: the
// index doesn't have per-content overrides (no merge step) and uses a
// flat BlockInstance[] shape (not placement-tagged ArticleBlock[]).

interface IndexConfig {
  aboveBlocks?: BlockInstance[];
  belowBlocks?: BlockInstance[];
}

interface IndexZoneProps {
  config: IndexConfig | undefined;
  themeId?: string | null;
  className?: string;
  style?: CSSProperties;
}

/** Above the index. Renders nothing if no blocks configured. */
export function IndexAboveBlocks({ config, themeId, className, style }: IndexZoneProps): ReactNode {
  const items = config?.aboveBlocks ?? [];
  if (items.length === 0) return null;
  return maybeWrap(<BlockList items={items} themeId={themeId} />, className, style);
}

/** Below the index. Renders nothing if no blocks configured. */
export function IndexBelowBlocks({ config, themeId, className, style }: IndexZoneProps): ReactNode {
  const items = config?.belowBlocks ?? [];
  if (items.length === 0) return null;
  return maybeWrap(<BlockList items={items} themeId={themeId} />, className, style);
}
